import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Grid,
  Avatar,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  LinearProgress,
  Stack,
  Alert,
  TablePagination
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  TwoWheeler,
  CheckCircle,
  Build,
  ElectricBolt,
  HighlightOff
} from '@mui/icons-material';
import { green, orange, blue, deepPurple, red, grey } from '@mui/material/colors';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';
import { getScooters, createScooter, updateScooter, deleteScooter } from '../services/riderService';

const ScooterManagement = () => {
  const [scooters, setScooters] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentScooter, setCurrentScooter] = useState(null);
  const [action, setAction] = useState('add');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Status options
  const statusOptions = [
    { value: 'available', label: 'Available', color: green[500], icon: <CheckCircle /> },
    { value: 'rented', label: 'Rented', color: blue[500], icon: <ElectricBolt /> },
    { value: 'inmaintenance', label: 'Maintenance', color: orange[500], icon: <Build /> },
    // Test = reserved/experiment scooter (should be visually distinct from "Unassembled")
    { value: 'test', label: 'Test', color: deepPurple[500], icon: <Build /> },
    { value: 'unassembled', label: 'Unassembled', color: grey[700], icon: <HighlightOff /> },
  ];

  useEffect(() => {
    fetchScooters();
  }, []);

  const fetchScooters = async () => {
    try {
      setLoading(true);
      const data = await getScooters();
      setScooters(data);
    } catch (err) {
      console.error('Error fetching scooters:', err);
      setError('Failed to load scooters');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCurrentScooter({
      ...currentScooter,
      [name]: value
    });
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  /** Format for datetime-local input: yyyy-MM-ddThh:mm. Empty if null or 1900-01-01. */
  const formatDateTimeLocal = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    if (d.getFullYear() === 1900 && d.getMonth() === 0 && d.getDate() === 1) return '';
    return d.toISOString().slice(0, 16);
  };

  const handleEdit = (scooter) => {
    setAction('edit');
    setCurrentScooter({
      ...scooter,
      last_service: formatDateForInput(scooter.last_service),
      last_git_pull: formatDateTimeLocal(scooter.last_git_pull)
    });
    setOpenDialog(true);
  };

  const handleSubmit = async () => {
    try {
      setError('');
      // Validate required fields
      if (!currentScooter.status) {
        setError('Status is required');
        return;
      }
      const serialTrimmed = (currentScooter.serial || '').trim();
      if (!serialTrimmed) {
        setError('Serial Number is required');
        return;
      }
      // Check for duplicate serial (trimmed)
      const duplicate = scooters.find(s => (s.serial || '').trim() === serialTrimmed && s.scooter_id !== currentScooter.scooter_id);
      if (duplicate) {
        setError('Serial Number must be unique');
        return;
      }
      // Always submit trimmed serial; ensure last_git_pull is sent (empty => backend uses 1900-01-01)
      const scooterToSubmit = {
        ...currentScooter,
        serial: serialTrimmed,
        last_git_pull: currentScooter.last_git_pull || ''
      };
      if (action === 'add') {
        await createScooter(scooterToSubmit);
      } else {
        await updateScooter(currentScooter.scooter_id, scooterToSubmit);
      }
      await fetchScooters();
      setOpenDialog(false);
    } catch (err) {
      console.error('Error saving scooter:', err);
      if (err.response?.data?.error?.includes('Serial number must be unique')) {
        setError('Serial Number must be unique');
      } else {
        setError(err.response?.data?.error || 'Failed to save scooter');
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this scooter?')) {
      return;
    }
    
    try {
      await deleteScooter(id);
      await fetchScooters();
    } catch (err) {
      console.error('Error deleting scooter:', err);
      setError(err.response?.data?.error || 'Failed to delete scooter');
    }
  };

  // Search/filter logic
  const filteredScooters = scooters.filter(scooter => {
    const searchText = `${scooter.scooter_id} ${scooter.model || ''} ${scooter.serial || ''} ${scooter.status || ''} ${scooter.campus_location || ''} ${scooter.reason || ''}`.toLowerCase();
    return searchText.includes(search.toLowerCase());
  });
  const paginatedScooters = filteredScooters.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        position: 'relative'
      }}>
      <Navbar />
      <Container maxWidth="xl" sx={{ pt:10, mt:4, mb:6, flex:1, pb: 8 }}>
      
      {/* Main content Paper - wrapping header, search, alerts, and table */}
      <Paper elevation={3} sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        p: 4, // Padding for the entire content area
        mb: 2 // Margin bottom for spacing from footer or next section
      }}>
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h4" component="h1" sx={{
                fontWeight: 700,
                color: deepPurple[800],
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 1
              }}>
                <TwoWheeler fontSize="large" />
                Scooter Fleet
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Manage and track your scooter inventory
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                setAction('add');
                setCurrentScooter({
                  model: '',
                  status: 'available',
                    battery: 100,
                    last_service: new Date().toISOString().split('T')[0]
                });
                setOpenDialog(true);
              }}
              sx={{
                bgcolor: deepPurple[600],
                '&:hover': { bgcolor: deepPurple[800] },
                px: 4,
                py: 1.5,
                height: 'fit-content'
              }}
            >
              New Scooter
            </Button>
          </Stack>
          <Divider sx={{ mt: 3 }} />
        </Box>

        {/* Search Bar */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'nowrap', alignItems: 'center' }}>
          <TextField
            label="Search"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            size="small"
            fullWidth
          />
        </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
        
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: deepPurple[50] }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Scooter</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Model</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Serial</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Battery</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Last Service</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Last Git Pull</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>IP Address</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Campus Location</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Reason</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Manual Scooter ID</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Email</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center">Loading...</TableCell>
                  </TableRow>
                ) : filteredScooters.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center">No scooters found</TableCell>
                  </TableRow>
                ) : (
                  paginatedScooters.map((scooter) => {
                const status = statusOptions.find(s => s.value === scooter.status);
                return (
                  <TableRow 
                        key={scooter.scooter_id}
                      hover
                      sx={{ '&:last-child td': { borderBottom: 0 } }}
                    >
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar sx={{ 
                            bgcolor: deepPurple[100], 
                            color: deepPurple[800],
                            width: 42,
                            height: 42
                          }}>
                            <TwoWheeler />
                          </Avatar>
                              <Typography fontWeight={500}>{scooter.scooter_id}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                              {scooter.model || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                              {scooter.serial || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                              icon={status?.icon}
                              label={status?.label || scooter.status}
                          sx={{ 
                                backgroundColor: `${status?.color}10`,
                                color: status?.color,
                            fontWeight: 600,
                                border: `1px solid ${status?.color}30`,
                            px: 1,
                            '& .MuiChip-icon': {
                                  color: status?.color
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <LinearProgress 
                            variant="determinate" 
                                value={scooter.battery || 0} 
                            sx={{ 
                              width: '100%',
                              height: 8,
                              borderRadius: 4,
                              bgcolor: 'grey.200',
                              '& .MuiLinearProgress-bar': {
                                    bgcolor: (scooter.battery || 0) > 20 ? green[500] : orange[500],
                                borderRadius: 4
                              }
                            }} 
                          />
                          <Typography variant="body2" fontWeight={500}>
                                {scooter.battery || 0}%
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                              {scooter.last_service ? new Date(scooter.last_service).toLocaleDateString() : 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                              {scooter.last_git_pull ? new Date(scooter.last_git_pull).toLocaleString() : 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {scooter.ip_address || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {scooter.campus_location || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {scooter.reason || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {scooter.manual_scooter_id || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {scooter.email || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Tooltip title="Edit">
                            <IconButton color="primary" onClick={() => handleEdit(scooter)}>
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton color="error" onClick={() => handleDelete(scooter.scooter_id)}>
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                );})
                )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filteredScooters.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </TableContainer>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{action === 'add' ? 'Add New Scooter' : 'Edit Scooter'}</DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Model"
                name="model"
                value={currentScooter?.model || ''}
                onChange={handleChange}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Serial Number"
                name="serial"
                value={currentScooter?.serial || ''}
                onChange={handleChange}
                margin="normal"
                required
                error={!!error.includes('Serial Number must be unique')}
                helperText={error.includes('Serial Number must be unique') ? error : ''}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal" required>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  name="status"
                  value={currentScooter?.status || ''}
                  onChange={e => {
                    handleChange(e);
                    const v = e.target.value;
                    if (v !== 'inmaintenance' && v !== 'test') {
                      setCurrentScooter(cs => ({ ...cs, reason: '' }));
                    }
                  }}
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Battery Level"
                name="battery"
                type="number"
                value={currentScooter?.battery || 0}
                onChange={handleChange}
                margin="normal"
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Last Service Date"
                name="last_service"
                type="date"
                value={currentScooter?.last_service || ''}
                onChange={handleChange}
                margin="normal"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Last Git Pull"
                name="last_git_pull"
                type="datetime-local"
                value={currentScooter?.last_git_pull ?? ''}
                onChange={handleChange}
                margin="normal"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="IP Address"
                name="ip_address"
                value={currentScooter?.ip_address || ''}
                onChange={handleChange}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Campus Location"
                name="campus_location"
                value={currentScooter?.campus_location || ''}
                onChange={handleChange}
                margin="normal"
              />
            </Grid>
            {/* Reason only for Maintenance or Test */}
            {(currentScooter?.status === 'inmaintenance' || currentScooter?.status === 'test') && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Reason"
                  name="reason"
                  value={currentScooter?.reason || ''}
                  onChange={handleChange}
                  margin="normal"
                  multiline
                  rows={2}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Manual Scooter ID"
                name="manual_scooter_id"
                value={currentScooter?.manual_scooter_id || ''}
                onChange={handleChange}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                value={currentScooter?.email || ''}
                onChange={handleChange}
                margin="normal"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="secondary">Cancel</Button>
          <Button onClick={handleSubmit} color="primary" variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Container>
    <Box sx={{ 
        width: '100%',
        bgcolor: 'background.paper',
        boxShadow: '0 -2px 5px rgba(0,0,0,0.1)',
          position: 'relative',
          mt: 'auto'
      }}>
        <Footer />
      </Box>
    </Box>
  );
};

export default ScooterManagement;